import { useState, useRef, useEffect } from "react";
import { useChat } from "@/context/ChatContext";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from '@supabase/supabase-js';
import { DocumentAnalysis, ActionSuggestion } from "@/context/chat/documentAnalysisService";
import { createRouteAwareChannelName, shouldProcessEvent, isInRoute } from "@/utils/routeManager";
import { documentContextManager } from "@/context/chat/documentContextManager";
import EmptyState from "./chat/EmptyState";
import MessagesList from "./chat/MessagesList";
import { detectDocumentIntent, createDocumentFromChat, cleanContentForEditor } from "@/services/documentTransferService";
import { useNavigate, useParams } from "react-router-dom";
import { StyleIndicator } from "./chat/StyleIndicator";
import "@/styles/chatButtons.css";

import ChatInput from "./chat/ChatInput";
import DocumentSummaryDisplay from "./chat/DocumentSummaryDisplay";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Lightbulb, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const ChatInterface = () => {
  const { messages, isProcessing, currentConversation, sendMessage, createNewConversation, updateConversationTemplate } = useChat();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { conversationId: cidParam } = useParams();
  
  // 🔒 GUARDA DE ROTA: Estabilizar hasUrlConversation
  const previousCidParam = useRef<string | undefined>(cidParam);
  const hasUrlConversationRef = useRef(Boolean(cidParam));
  
  // Atualizar ref apenas quando cidParam realmente mudar
  if (cidParam && cidParam !== previousCidParam.current) {
    hasUrlConversationRef.current = true;
    previousCidParam.current = cidParam;
  }
  
  const hasUrlConversation = hasUrlConversationRef.current;
  const activeConversationId = cidParam ?? currentConversation?.id ?? null;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [isChatBlocked, setIsChatBlocked] = useState(false);
  const [isCheckingChatAccess, setIsCheckingChatAccess] = useState(true);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [documentAnalysis, setDocumentAnalysis] = useState<DocumentAnalysis | null>(null);
  const [currentChatStatus, setCurrentChatStatus] = useState<boolean | null>(null);
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState<'thinking' | 'extracting' | 'analyzing' | 'completed' | null>(null);
  const [documentSummaryData, setDocumentSummaryData] = useState<{
    summary: string;
    documentType: string;
    keyData?: any;
    conversationId?: string;
  } | null>(null);
  
  
  // Estados para controlar conteúdo das divs (sem localStorage para conteúdo)
  const [hasDocumentContent, setHasDocumentContent] = useState(false);
  const [hasSuggestionsContent, setHasSuggestionsContent] = useState(false);
  
  // APENAS preferências de UI no localStorage
  const [isDocumentExpanded, setIsDocumentExpanded] = useState(false);
  const [isSuggestionsExpanded, setIsSuggestionsExpanded] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();
  
  // Controles ESPECÍFICOS para chat com verificação rigorosa de rota
  const listenerSetupRef = useRef(false);
  const accessCheckInProgress = useRef(false);
  const lastStatusCheck = useRef<number>(0);
  const chatChannelRef = useRef<any>(null);
  const stableChatState = useRef(false);
  const chatRouteActive = useRef(false);
  
  // 🔒 CORREÇÃO CRÍTICA: Realtime com guarda de unicidade
  const messagesChannelRef = useRef<RealtimeChannel | null>(null);

  const fetchUserChatAccess = async () => {
    const currentPath = window.location.pathname;
    const isInChatRoute = currentPath.startsWith('/chat');
    
    if (!user || accessCheckInProgress.current || !isInChatRoute) {
      if (!user) {
        setIsChatBlocked(true);
        setIsCheckingChatAccess(false);
      }
      return;
    }
    
    // Evitar múltiplas verificações simultâneas com timeout específico para chat
    const now = Date.now();
    if (now - lastStatusCheck.current < 8000) { // 8 segundos para chat
      return;
    }
    
    lastStatusCheck.current = now;
    accessCheckInProgress.current = true;
    
    try {
      setIsCheckingChatAccess(true);
      
      const { data, error } = await supabase
        .from('users')
        .select('status_chat')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Chat: Error checking chat access:', error);
        setIsChatBlocked(true);
      } else {
        const newChatStatus = !!data.status_chat;
        setIsChatBlocked(!newChatStatus);
        setCurrentChatStatus(newChatStatus);
        stableChatState.current = true;
      }
    } catch (error) {
      console.error('Chat: Error in fetchUserChatAccess:', error);
      setIsChatBlocked(true);
    } finally {
      setIsCheckingChatAccess(false);
      accessCheckInProgress.current = false;
    }
  };

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isInChatRoute = currentPath.startsWith('/chat');
    
    if (!isInChatRoute) {
      return;
    }
    
    // Verificação inicial APENAS para rota de chat
    fetchUserChatAccess();
    
    // Configurar listener ÚNICO apenas para o usuário específico E rota de chat
    if (user && !listenerSetupRef.current && isInChatRoute) {
      listenerSetupRef.current = true;
      chatRouteActive.current = true;
      
      const channelName = createRouteAwareChannelName(`user-chat-status-${user.id}`);
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { 
            event: 'UPDATE', 
            schema: 'public', 
            table: 'users',
            filter: `id=eq.${user.id}` 
          },
          (payload) => {
            // Verificação TRIPLA: contexto chat, rota correta, estado não estabilizado
            if (shouldProcessEvent('/chat') && isInRoute('/chat') && !stableChatState.current) {
              const newChatStatus = !!payload.new.status_chat;
              const oldChatStatus = currentChatStatus;
              
              setIsChatBlocked(!newChatStatus);
              setCurrentChatStatus(newChatStatus);
              
              // Toast apenas para mudanças significativas depois da estabilização
              if (oldChatStatus !== null && oldChatStatus !== newChatStatus) {
                toast({
                  title: newChatStatus ? "Acesso Liberado" : "Acesso Bloqueado",
                  description: newChatStatus 
                    ? "Seu acesso ao chat foi restaurado." 
                    : "Seu acesso ao chat foi bloqueado temporariamente.",
                  variant: newChatStatus ? "default" : "destructive",
                });
              }
            }
          }
        )
        .subscribe();
      
      chatChannelRef.current = channel;
      
      return () => {
        if (chatChannelRef.current) {
          supabase.removeChannel(chatChannelRef.current);
          chatChannelRef.current = null;
        }
        listenerSetupRef.current = false;
        stableChatState.current = false;
        chatRouteActive.current = false;
      };
    }
  }, [user?.id, window.location.pathname]);

  useEffect(() => {
    const handleRouteChange = () => {
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/chat') && chatRouteActive.current) {
        if (chatChannelRef.current) {
          supabase.removeChannel(chatChannelRef.current);
          chatChannelRef.current = null;
          listenerSetupRef.current = false;
          stableChatState.current = false;
          chatRouteActive.current = false;
        }
      }
    };

    // React Router já gerencia mudanças de rota
    return () => {
      // Cleanup será feito pelo React Router
    };
  }, []);

  useEffect(() => {
    return () => {
      if (chatChannelRef.current) {
        supabase.removeChannel(chatChannelRef.current);
        chatChannelRef.current = null;
        listenerSetupRef.current = false;
        stableChatState.current = false;
        chatRouteActive.current = false;
      }
    };
  }, []);

  // 🔒 REALTIME DEDUPE: Assinatura única por conversa com cleanup obrigatório
  useEffect(() => {
    if (!activeConversationId) {
      // Limpar canal se não há conversa ativa
      if (messagesChannelRef.current) {
        console.log('🧹 [Realtime] Limpando canal - sem conversa ativa');
        supabase.removeChannel(messagesChannelRef.current);
        messagesChannelRef.current = null;
      }
      return;
    }

    // 🔒 SEMPRE limpar canal anterior ANTES de criar novo (evita duplicação)
    if (messagesChannelRef.current) {
      console.log('🧹 [Realtime] Limpando canal anterior antes de criar novo');
      supabase.removeChannel(messagesChannelRef.current);
      messagesChannelRef.current = null;
    }

    console.log('📡 [Realtime] Criando assinatura para conversa:', activeConversationId);

    const channel = supabase
      .channel(`messages:${activeConversationId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${activeConversationId}`
      }, (payload) => {
        const newMsg = payload.new as Record<string, any>;
        console.log('📥 [Realtime] Nova mensagem recebida:', payload.eventType, newMsg?.id);
        
        // Usar setMessages que já tem dedupe via messagesMapRef
        if (payload.eventType === 'INSERT' && newMsg && newMsg.id) {
          
          // Dedupe: Verificar se já existe antes de adicionar
          const msgExists = messages.find(m => m.id === newMsg.id);
          if (msgExists) {
            console.log('⚠️ [Realtime] Mensagem já existe, ignorando duplicata:', newMsg.id);
            return;
          }
          
          console.log('✅ [Realtime] Nova mensagem adicionada:', newMsg.id);
        }
      })
      .subscribe((status) => {
        console.log('📡 [Realtime] Status assinatura:', status);
      });

    messagesChannelRef.current = channel;

    // 🔒 Cleanup obrigatório ao desmontar ou trocar conversa
    return () => {
      if (messagesChannelRef.current) {
        console.log('🧹 [Realtime] Cleanup: Removendo canal');
        supabase.removeChannel(messagesChannelRef.current);
        messagesChannelRef.current = null;
      }
    };
  }, [activeConversationId, messages]);

  // APENAS preferências de UI no localStorage (expandido/colapsado)
  useEffect(() => {
    if (currentConversation?.id) {
      const storageKey = `chat-ui-preferences-${currentConversation.id}`;
      const savedPreferences = localStorage.getItem(storageKey);
      
      if (savedPreferences) {
        try {
          const parsed = JSON.parse(savedPreferences);
          setIsDocumentExpanded(parsed.isDocumentExpanded || false);
          setIsSuggestionsExpanded(parsed.isSuggestionsExpanded || false);
        } catch (error) {
          console.warn('Error loading UI preferences from localStorage:', error);
        }
      }
    }
  }, [currentConversation?.id]);

  // Salvar APENAS preferências de UI no localStorage
  useEffect(() => {
    if (currentConversation?.id) {
      const storageKey = `chat-ui-preferences-${currentConversation.id}`;
      const preferencesToSave = {
        isDocumentExpanded,
        isSuggestionsExpanded
      };
      localStorage.setItem(storageKey, JSON.stringify(preferencesToSave));
    }
  }, [currentConversation?.id, isDocumentExpanded, isSuggestionsExpanded]);

  // Track if user has uploaded a file in this conversation
  useEffect(() => {
    setHasUploadedFile(!!selectedFile);
  }, [selectedFile]);

  // 🔒 GUARDA DE ROTA: Persiste último conversationId válido
  const lastValidConversationId = useRef<string | null>(null);
  
  // FUNÇÃO CORRIGIDA: Carregamento robusto do banco de dados
  useEffect(() => {
    const loadSavedAnalysis = async () => {
      // 🔒 VERIFICAÇÃO: Só limpar se realmente em /chat sem ID
      const isRootChatRoute = window.location.pathname === '/chat';
      
      if (!user?.id) {
        console.log('📭 No user, clearing states');
        // Só limpar se realmente não houver usuário E estiver em /chat
        if (isRootChatRoute) {
          setSuggestedQuestions([]);
          setDocumentSummaryData(null);
          setHasDocumentContent(false);
          setHasSuggestionsContent(false);
          lastValidConversationId.current = null;
        }
        return;
      }

      // 🔒 CORREÇÃO CIRÚRGICA: Só limpar estados se realmente em /chat raiz
      const currentPath = window.location.pathname;
      const isReallyAtChatRoot = currentPath === '/chat' && !cidParam;
      
      if (!hasUrlConversation) {
        // Se temos último ID válido E não estamos na raiz, manter estados
        if (lastValidConversationId.current && !isReallyAtChatRoot) {
          console.log('🔒 Navigation in progress, keeping states (lastValidId:', lastValidConversationId.current, ')');
          return; // NÃO limpar - mantém contexto durante navegação
        }
        
        // Só limpar se realmente em /chat sem ID
        if (isReallyAtChatRoot) {
          console.log('📭 Really at /chat root, clearing states');
          setSuggestedQuestions([]);
          setDocumentSummaryData(null);
          setHasDocumentContent(false);
          setHasSuggestionsContent(false);
          lastValidConversationId.current = null;
          hasUrlConversationRef.current = false;
        }
        return;
      }

      if (!currentConversation?.id) {
        // Permitir renderização básica durante criação de conversa
        return;
      }
      
      // 🔒 Salva último conversationId válido
      lastValidConversationId.current = currentConversation.id;

      try {
        console.log('🔄 Loading saved analysis for conversation:', currentConversation.id);
        
        // ✅ CORREÇÃO 3: Reset states when conversation changes
        setSuggestedQuestions([]);
        setDocumentSummaryData(null);
        setHasDocumentContent(false);
        setHasSuggestionsContent(false);
        
        // Forçar carregamento do banco sempre, garantindo dados atualizados
        const session = await documentContextManager.loadFromDatabase(currentConversation.id, user.id);
        
        if (session?.lastAnalysis && session.lastAnalysis.suggestions.length > 0) {
          console.log('📥 Found saved analysis in database, restoring content');
          
          // DEFINIR CONTEÚDO REAL primeiro - filtrar sugestões válidas
          const questions = session.lastAnalysis.suggestions
            .map(suggestion => suggestion.question)
            .filter(q => q && q.trim().length > 0);
          
          setSuggestedQuestions(questions);
          
          if (session.documents.length > 0 && session.lastAnalysis.summary) {
            const documentContext = session.documents[0];
            setDocumentSummaryData({
              summary: session.lastAnalysis.summary,
              documentType: documentContext.documentType,
              keyData: documentContext.keyData,
              conversationId: currentConversation.id
            });
            setHasDocumentContent(true);
          }
          
          // DEPOIS definir flags de conteúdo baseado no que realmente existe
          setHasSuggestionsContent(questions.length > 0);
          
          // Auto-expandir quando há conteúdo removido - usuário controla manualmente
          if (session.lastAnalysis.summary) {
            setIsDocumentExpanded(true);
          }
          
          console.log('✅ Content restored from database:', {
            questionsCount: questions.length,
            hasSummary: !!session.lastAnalysis.summary,
            documentType: session.documents[0]?.documentType
          });
        } else {
          const hasValidConversation = Boolean(cidParam);
          if (!hasValidConversation) {
            console.log('📭 No URL conversation, clearing all states');
            setSuggestedQuestions([]);
            setDocumentSummaryData(null);
            setHasDocumentContent(false);
            setHasSuggestionsContent(false);
          } else {
            console.log('📝 Conversation exists (via URL) — keeping UI ready');
          }
        }
      } catch (error) {
        console.error('❌ Error loading saved analysis:', error);
        // Em caso de erro, limpar estados
        setSuggestedQuestions([]);
        setDocumentSummaryData(null);
        setHasDocumentContent(false);
        setHasSuggestionsContent(false);
      }
    };

    loadSavedAnalysis();
  }, [activeConversationId, user?.id]); // Use activeConversationId as source of truth

  // REMOVIDO: Fallback que estava conflitando com carregamento principal do banco

  const handleStartChat = async () => {
    if (currentConversation) {
      // Já está em uma conversa - sem foco forçado
      console.log('Chat already active - no forced focus');
    } else {
      // Não está em conversa, cria nova
      await createNewConversation();
      console.log('New conversation created - no forced focus');
    }
  };

  const triggerNativeFileSelector = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt';
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        // **CORREÇÃO CIRÚRGICA**: Processar igual ao handlePasteFile
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        const documentFiles = files.filter(file => !file.type.startsWith('image/'));
        
        // Para primeira imagem (compatibilidade com selectedFile)
        if (imageFiles.length > 0) {
          setSelectedFile(imageFiles[0]);
          
          // Se há múltiplas imagens, processar todas
          if (imageFiles.length > 1) {
            const event = new CustomEvent('multipleImagesSelected', { 
              detail: { files: imageFiles } 
            });
            window.dispatchEvent(event);
          }
        }
        
        // Para documentos: adicionar diretamente (ChatInput processará automaticamente)
        if (documentFiles.length > 0) {
          // Trigger o evento que o ChatInput irá capturar via onNonImageFileSelected
          const event = new CustomEvent('multipleDocumentsSelected', { 
            detail: { files: documentFiles } 
          });
          window.dispatchEvent(event);
        }
        
        toast({
          title: files.length > 1 ? "Arquivos Selecionados" : "Arquivo Selecionado",
          description: files.length > 1 
            ? `${files.length} arquivos prontos para análise.`
            : `${files[0].name} pronto para análise.`,
          variant: "default",
        });
      }
    };
    input.click();
  };

  const handleUploadDocument = async () => {
    if (currentConversation) {
      // Já está em uma conversa, apenas abre o seletor nativo
      triggerNativeFileSelector();
    } else {
      // Não está em conversa, cria nova
      await createNewConversation();
      setTimeout(() => {
        triggerNativeFileSelector();
      }, 100);
    }
  };

  const handleDocumentAnalysis = async (analysis: DocumentAnalysis) => {
    console.log('📄 Real document analysis received:', analysis);
    setDocumentAnalysis(analysis);
    
    if (currentConversation?.id && user?.id) {
      try {
        console.log('💾 Loading document context from database...');
        
        // Carregar contexto salvo do banco de dados
        const session = await documentContextManager.getDocumentContext(currentConversation.id, user.id);
        
        if (session?.lastAnalysis) {
          console.log('✅ Document context loaded successfully');
          
          // Mostrar resumo do documento
          setDocumentSummaryData({
            summary: session.lastAnalysis.summary,
            documentType: analysis.documentType,
            keyData: session.documents[0]?.keyData,
            conversationId: currentConversation.id
          });
          setHasDocumentContent(true);
          setIsDocumentExpanded(true);
          
          // Configurar sugestões contextuais
          const questions = session.lastAnalysis.suggestions.map(suggestion => suggestion.question);
          setSuggestedQuestions(questions);
          setHasSuggestionsContent(true);
          
          console.log(`🎯 Loaded ${questions.length} contextual suggestions`);
          
          toast({
            title: "Documento Analisado!",
            description: `Resumo e ${questions.length} sugestões contextuais carregadas.`,
            variant: "default",
          });
        } else {
          console.warn('⚠️ No document context found in database');
          toast({
            title: "Processamento Incompleto",
            description: "Documento processado mas contexto não foi salvo corretamente.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('❌ Error handling document analysis:', error);
        toast({
          title: "Erro na Análise",
          description: "Erro ao processar análise do documento.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSelectQuestion = (question: string) => {
    console.log('🎯 Contextual question selected:', question);
    
    setInputValue(question);
    // Removido foco forçado - deixar usuário controlar
    
    // Auto-send the message with context
    setTimeout(() => {
      sendMessage(question, null);
      setInputValue("");
      setIsSuggestionsExpanded(false); // Fechar apenas a visualização das sugestões após envio
    }, 100);
  };

  // Toggle suggestions visibility
  const toggleSuggestions = () => {
    setIsSuggestionsExpanded(!isSuggestionsExpanded);
  };

  // Toggle document summary visibility
  const toggleDocumentSummary = () => {
    setIsDocumentExpanded(!isDocumentExpanded);
  };

  const handleFileProcessingStart = () => {
    setIsFileProcessing(true);
    setProcessingStage('thinking');
  };

  const handleFileProcessingProgress = (stage: 'thinking' | 'extracting' | 'analyzing', progress: number) => {
    setProcessingStage(stage);
  };

  const handleFileProcessingEnd = () => {
    setIsFileProcessing(false);
    setProcessingStage('completed');
    setTimeout(() => {
      setProcessingStage(null);
    }, 2000);
  };


  if (isCheckingChatAccess) {
    return <div className="flex items-center justify-center p-6">Verificando acesso...</div>;
  }

  if (isChatBlocked) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Seu acesso ao chat está temporariamente bloqueado. 
            Por favor, entre em contato com o suporte para mais informações.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full relative overflow-hidden">

      {/* ÁREA SCROLLÁVEL - apenas mensagens e painéis */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {messages.length === 0 ? (
          <EmptyState 
            onStartChat={handleStartChat}
            onUploadDocument={handleUploadDocument}
          />
        ) : (
          <>
            {/* RESUMO DO DOCUMENTO - SEMPRE VISÍVEL */}
            <div className="w-full max-w-4xl mx-auto px-4 mb-4">
              {hasDocumentContent && documentSummaryData && documentSummaryData.conversationId === activeConversationId ? (
                <DocumentSummaryDisplay
                  data-tour="document-summary"
                  summary={documentSummaryData.summary}
                  documentType={documentSummaryData.documentType}
                  keyData={documentSummaryData.keyData}
                />
              ) : (
                <Alert className="bg-gray-50 border-gray-200 dark:bg-gray-950 dark:border-gray-800">
                  <AlertDescription className="text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📄</span>
                      <span className="font-medium">Nenhum documento analisado ainda</span>
                    </div>
                    <p className="text-sm mt-1 opacity-75">
                      Faça upload de um documento para ver o resumo executivo e dados extraídos aqui.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <MessagesList 
              data-tour="messages-area"
              messages={messages}
              isProcessing={isProcessing}
              suggestedQuestions={[]}
              onSelectQuestion={handleSelectQuestion}
              hasUploadedFile={hasUploadedFile}
            />

            {/* PAINEL DE SUGESTÕES - SEMPRE PRESENTE */}
            {isSuggestionsExpanded && (
              <div className="w-full max-w-4xl mx-auto px-4 mb-4">
                <div data-tour="suggested-questions" className={`rounded-xl p-6 border shadow-lg ${
                  hasSuggestionsContent 
                    ? "bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/50 dark:to-purple-950/50 border-blue-200 dark:border-blue-800"
                    : "bg-gray-50 dark:bg-gray-950 border-gray-200 dark:border-gray-800"
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Lightbulb className={`h-5 w-5 ${
                        hasSuggestionsContent 
                          ? "text-blue-600 dark:text-blue-400" 
                          : "text-gray-400 dark:text-gray-600"
                      }`} />
                      <h4 className={`text-base font-semibold ${
                        hasSuggestionsContent 
                          ? "text-blue-900 dark:text-blue-100" 
                          : "text-gray-600 dark:text-gray-400"
                      }`}>
                        {hasSuggestionsContent 
                          ? "💡 Perguntas Inteligentes sobre seu Documento:" 
                          : "💡 Aguardando Documento para Sugestões"
                        }
                      </h4>
                    </div>
                    <Button
                      onClick={toggleSuggestions}
                      size="sm"
                      variant="ghost"
                      className={`rounded-full ${
                        hasSuggestionsContent 
                          ? "text-blue-600 hover:text-blue-800 hover:bg-blue-100" 
                          : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      ✕
                    </Button>
                  </div>
                  
                  {hasSuggestionsContent ? (
                    <>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-4 opacity-90 leading-relaxed">
                        Baseado na análise inteligente do seu documento, posso responder especificamente sobre estes tópicos importantes:
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {suggestedQuestions.map((question, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            className="text-sm h-auto py-3 px-4 bg-white dark:bg-gray-900 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/50 hover:border-blue-400 dark:hover:border-blue-600 text-blue-800 dark:text-blue-200 hover:text-blue-900 dark:hover:text-blue-100 transition-all duration-200 shadow-sm hover:shadow-md rounded-lg min-w-0"
                            onClick={() => handleSelectQuestion(question)}
                          >
                            <span className="mr-2 text-base shrink-0">🎯</span>
                            <span className="line-clamp-3 text-left leading-snug break-words min-w-0">{question}</span>
                          </Button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 opacity-90 leading-relaxed">
                      Após fazer upload de um documento, sugestões inteligentes e contextuais aparecerão aqui para ajudar você a fazer as perguntas mais relevantes sobre o conteúdo.
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ÁREA FIXA INFERIOR - botões, progress e input */}
      <div className="flex-shrink-0">
        {/* WRAPPER EXCLUSIVO DOS BOTÕES DO CHAT GERAL */}
        <div className="cg-buttons-wrap">
          <StyleIndicator
            currentTemplate={currentConversation?.metadata?.template || 'lovable_gpt'}
            onTemplateChange={async (templateName) => {
              if (currentConversation && updateConversationTemplate) {
                await updateConversationTemplate(currentConversation.id, templateName);
              }
            }}
          />
          <button
            type="button"
            className="cg-btn"
            onClick={toggleSuggestions}
            aria-label="Abrir sugestões"
          >
            <Lightbulb className="cg-icon" />
            <span className="cg-label">
              {hasSuggestionsContent 
                ? `Sugestões (${suggestedQuestions.length})` 
                : "Sugestões (0)"
              }
            </span>
          </button>
        </div>

        {showProgress && (
          <div className="mx-4 mb-2">
            <Progress value={extractionProgress} />
          </div>
        )}

        <ChatInput 
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          inputValue={inputValue}
          setInputValue={setInputValue}
          textareaRef={textareaRef}
          isUserApproved={!isChatBlocked && !isFileProcessing}
          onDocumentAnalysis={handleDocumentAnalysis}
          triggerNativeFileSelector={triggerNativeFileSelector}
        />
      </div>
    </div>
  );
};

export default ChatInterface;
